// src/utils/TextToSpeech.js
class TextToSpeech {
  constructor() {
    this.voices = []
    this.isVoicesLoaded = false
    this.queue = []
    this.isSpeaking = false
    this.defaultOptions = {
      lang: 'ru-RU',
      rate: 0.9,
      pitch: 1,
      volume: 1,
      priority: 'normal',
    }

    this.initializeVoices()
  }

  initializeVoices() {
    this.voices = window.speechSynthesis.getVoices()
    if (this.voices.length > 0) {
      this.isVoicesLoaded = true
    }

    window.speechSynthesis.onvoiceschanged = () => {
      this.voices = window.speechSynthesis.getVoices()
      this.isVoicesLoaded = true
      console.log('Speech voices updated:', this.voices)
    }
  }

  speak(text, options = {}) {
    if (!this.isSupported()) {
      console.error('Speech synthesis is not supported')
      return
    }

    const mergedOptions = { ...this.defaultOptions, ...options }

    if (!this.isVoicesLoaded) {
      console.warn('Voices not loaded yet. Adding to queue...')
      this.queue.unshift({ text, options: mergedOptions })
      this.initializeVoices()
      return
    }

    if (options.priority === 'high') {
      this.queue.unshift({ text, options: mergedOptions })
    } else {
      this.queue.push({ text, options: mergedOptions })
    }

    if (!this.isSpeaking) {
      this.processQueue()
    }
  }

  processQueue() {
    if (this.queue.length === 0) {
      this.isSpeaking = false
      return
    }

    this.isSpeaking = true
    const { text, options } = this.queue.shift()

    const voice =
      this.voices.find((v) => v.lang.includes(options.lang || 'ru-RU')) ||
      this.voices.find((v) => v.lang.includes('ru')) ||
      null

    if (!voice) {
      console.error(`Voice for language ${options.lang} not found`)
      this.processQueue()
      return
    }

    const utterance = new SpeechSynthesisUtterance(text)
    utterance.voice = voice
    utterance.lang = options.lang || 'ru-RU'
    utterance.rate = options.rate || 0.9
    utterance.pitch = options.pitch || 1
    utterance.volume = options.volume || 1

    if (options.onStart) {
      utterance.onstart = options.onStart
    }

    utterance.onend = () => {
      if (options.onEnd) {
        options.onEnd()
      }
      this.processQueue()
    }

    utterance.onerror = (event) => {
      console.error('Speech error:', event)
      if (options.onError) {
        options.onError(event)
      }
      this.processQueue()
    }

    this.stopImmediate()
    window.speechSynthesis.speak(utterance)
  }

  stop() {
    this.queue = []
    this.stopImmediate()
  }

  stopImmediate() {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel()
      this.isSpeaking = false
    }
  }

  pause() {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause()
    }
  }

  resume() {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume()
    }
  }

  getVoices() {
    return this.voices
  }

  isSupported() {
    return 'speechSynthesis' in window
  }

  setDefaultOptions(options) {
    this.defaultOptions = { ...this.defaultOptions, ...options }
  }

  getQueueLength() {
    return this.queue.length
  }

  clearQueue() {
    this.queue = []
  }
}

// Создаем и экспортируем singleton-экземпляр
const textToSpeech = new TextToSpeech()
export default textToSpeech
